import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@pentabd/ui";

import { InputTextRow, SelectControllerRow } from "@components";

import { useGeoData } from "@hooks/useGeoData";
import { useLanguage } from "@hooks/useLanguage";

import { FORM_FIELDS } from "@constants/form";
import { GEO_DATA } from "@constants/geo-data";

import { getConvertedGeoData } from "@helpers/geoDataConvert";
import { PermanentAddressType } from "@type/HRM/family";
import {
  PermanentAddressFormDataType,
  permanentAddressValidation,
} from "@validations/HRM/employee/familyInfoValidation";

interface PermanentAddressPropsType {
  permanentAddressInfo: PermanentAddressType;
  changePermanentAddressInfo: (data: PermanentAddressType) => void;
  getFamilyInfoRequestOngoing: boolean;
  updatePermanentAddressRequestOngoing: boolean;
  isProfileDisabled: boolean;
  canCreateEmployee: boolean;
  canEditEmployee: boolean;
}

const { DISTRICTS } = GEO_DATA;
const {
  FAMILY_INFO: { PERMANENT_ADDRESS },
} = FORM_FIELDS.EMPLOYEE;

const PermanentAddressForm = ({
  isProfileDisabled,
}: {
  isProfileDisabled: boolean;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: districts } = useGeoData(DISTRICTS);

  const {
    register,
    formState: { errors },
    setValue,
    control,
  } = useFormContext<PermanentAddressFormDataType>();

  return (
    <div className="d-flex flex-column gap-8">
      <Text weight="semibold" color="title" size="lg">
        {t("EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.HEADING")}
      </Text>

      <div className="d-flex flex-column gap-8">
        <InputTextRow
          register={{ ...register(PERMANENT_ADDRESS.VILLAGE_HOUSE_ROAD) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.VILLAGE_HOUSE_ROAD"
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(PERMANENT_ADDRESS.POST_OFFICE) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.POST_OFFICE"
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(PERMANENT_ADDRESS.POLICE_STATION) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.POLICE_STATION"
          error={errors}
          required
          disabled={isProfileDisabled}
        />
        <SelectControllerRow
          label="EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.DISTRICT"
          name={PERMANENT_ADDRESS.DISTRICT}
          error={errors}
          register={{ ...register(PERMANENT_ADDRESS.DISTRICT) }}
          options={getConvertedGeoData(districts, language)}
          setValue={setValue}
          control={control}
          isSearchable
          required
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(PERMANENT_ADDRESS.TELEPHONE) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.TELEPHONE"
          error={errors}
          disabled={isProfileDisabled}
        />
        <InputTextRow
          register={{ ...register(PERMANENT_ADDRESS.MOBILE) }}
          label="EMPLOYEE.FAMILY_INFO.FORM.PERMANENT_ADDRESS_INFO.MOBILE"
          error={errors}
          disabled={isProfileDisabled}
          required
        />
      </div>
    </div>
  );
};

const PermanentAddress = ({
  permanentAddressInfo,
  changePermanentAddressInfo,
  getFamilyInfoRequestOngoing,
  updatePermanentAddressRequestOngoing,
  isProfileDisabled,
  canCreateEmployee,
  canEditEmployee,
}: PermanentAddressPropsType) => {
  const { t } = useTranslation();
  const [disabledProfile, setDisabledProfile] = useState<boolean>(false);
  const methods = useForm<PermanentAddressType>({
    resolver: yupResolver(permanentAddressValidation),
    values: permanentAddressInfo as PermanentAddressType,
  });

  const { handleSubmit, reset } = methods;
  const onSubmit: SubmitHandler<PermanentAddressType> = (
    data: PermanentAddressType,
  ) => {
    changePermanentAddressInfo(data);
  };

  let isLoading =
    getFamilyInfoRequestOngoing || updatePermanentAddressRequestOngoing;

  useEffect(() => reset(permanentAddressInfo), [permanentAddressInfo, reset]);

  useEffect(() => {
    //for create
    if (
      (permanentAddressInfo?.id === null || permanentAddressInfo === null) &&
      canCreateEmployee
    ) {
      setDisabledProfile(true);
    }
    //for edit
    if (
      permanentAddressInfo?.id !== null &&
      permanentAddressInfo !== null &&
      canEditEmployee
    ) {
      setDisabledProfile(true);
    }
  }, [permanentAddressInfo, canCreateEmployee, canEditEmployee]);

  return (
    <FormProvider {...methods}>
      <form className="container" onSubmit={handleSubmit(onSubmit)}>
        <PermanentAddressForm
          isProfileDisabled={isProfileDisabled || !disabledProfile}
        />

        <div className="row py-10">
          <div className="text-end col-12 col-lg-10">
            <Button
              loading={isLoading}
              htmlType="submit"
              fill="outline"
              type="primary"
              size="sm"
              disabled={isProfileDisabled || !disabledProfile}
            >
              {t("EMPLOYEE.FAMILY_INFO.FORM.SAVE_BUTTON")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default PermanentAddress;
